/**
 * LEAD-SOURCE-01 (DEC-NEW-02) — where a customer came from. ESC tracks it as the
 * invoice "sales sort" code (mirror_sales.sort); we mirror those into a managed
 * `lead_source` picklist (admin can add/rename/deactivate). Per customer we
 * DERIVE the original source (earliest invoice's sort) + latest (HubSpot's
 * original-vs-latest attribution model) and allow a Pegasus-native override.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface LeadSourceOption {
  code: string;
  label: string;
  category: string | null;
  active: boolean;
  sort_order: number;
}

export function useLeadSourceOptions() {
  return useQuery({
    queryKey: ["lead_source_options"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<LeadSourceOption[]> => {
      const { data, error } = await supabase
        .from("lead_source")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("label", { ascending: true });
      if (error) throw error;
      return (data ?? []) as LeadSourceOption[];
    },
  });
}

export interface CustomerLeadSource {
  originalCode: string | null;
  originalDate: string | null;
  /**
   * Earliest non-CUR/OTHER code — NOT necessarily where the customer came
   * from. ESC's office codes tech-recommended work "TECH" on long-existing
   * customers, so for the 12.5k customers whose first invoice says CUR
   * ("existing customer"), this is just the first invoice that happened to
   * carry a real-looking code — possibly decades after they arrived. The
   * 2026-06-10 audit: presenting this as the headline made ~3,083 customers
   * read "Tech-generated" (true first-invoice TECH customers: 450). Display
   * it only as a clearly-labeled "first recorded" hint, never as the source.
   */
  specificCode: string | null;
  specificDate: string | null;
  latestCode: string | null;
  latestDate: string | null;
  overrideCode: string | null; // a human correction, if set
}

export function useCustomerLeadSource(custno: string | undefined) {
  return useQuery({
    queryKey: ["customer_lead_source", custno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CustomerLeadSource> => {
      const [{ data: rpc }, { data: ov }] = await Promise.all([
        supabase.rpc("customer_lead_source", { p_custno: custno as string }),
        supabase
          .from("customer_lead_override")
          .select("lead_source_code")
          .eq("custno", custno as string)
          .maybeSingle(),
      ]);
      const r = (Array.isArray(rpc) ? rpc[0] : rpc) as
        | {
            original_code: string | null;
            original_date: string | null;
            specific_code: string | null;
            specific_date: string | null;
            latest_code: string | null;
            latest_date: string | null;
          }
        | undefined;
      return {
        originalCode: r?.original_code ?? null,
        originalDate: r?.original_date ?? null,
        specificCode: r?.specific_code ?? null,
        specificDate: r?.specific_date ?? null,
        latestCode: r?.latest_code ?? null,
        latestDate: r?.latest_date ?? null,
        overrideCode: (ov as { lead_source_code?: string } | null)?.lead_source_code ?? null,
      };
    },
  });
}

export function useSetCustomerLeadOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; code: string | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("customer_lead_override").upsert({
        custno: v.custno,
        lead_source_code: v.code,
        set_by: u?.user?.email ?? null,
        updated_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["customer_lead_source", v.custno] }),
  });
}

export function useAddLeadSourceOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { code: string; label: string }) => {
      const code = v.code.trim().toUpperCase().slice(0, 12);
      const { error } = await supabase
        .from("lead_source")
        .insert({ code, label: v.label.trim(), category: "custom", sort_order: 70 });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead_source_options"] }),
  });
}
